/**
 * LeadRecordPage — the Screen-2 FULL-PAGE 3-column lead record (Phase 3b,
 * AD-046). It is the second SHAPE of the same Leads "Lenses" surface: the
 * peek panel (<LeadDetailDrawer>) is the quick triage shape, this is the
 * deep-work shape. Both flow through the SAME `onChange`→`updateLead` write
 * seam and the SAME guarded stage path (<LeadStageStrip>) — there is NO
 * parallel write path here.
 *
 * Routing: rendered by <LeadsLenses> ONLY when the route param `:id` is
 * present (`/leads/:id`). Because `/leads` and `/leads/:id` render the
 * identical <LeadsGate/> element, React Router keeps LeadsLenses mounted
 * across the two paths, so selection / data / scroll persist automatically.
 * `onBack` returns to the board (`/leads`); selection is preserved, so the
 * peek reopens for the same lead.
 *
 * Desktop: a 24% / 1fr / 26% grid; each column scrolls its own body (one
 * calm scroll per region, no nested scrollboxes). Mobile (`isMobile`):
 * the three regions STACK vertically in one column. Token-based styling
 * approximates the mockup; pixel-perfection is not required, the real
 * full-width desktop layout is.
 */
import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link, useNavigate, type NavigateFunction } from 'react-router-dom';
import {
  fetchThreadLookupForLead,
  type LeadThreadRef,
} from '../../lib/leadThreadJoin';
import { fmtClock } from '../../lib/messagesStore';
import { listenQuotesForLead } from '../../lib/quoteStore';
import {
  QUOTE_STATUS_LABEL,
  quoteAdjustments,
  quoteMoney,
  quoteTotals,
  type Quote,
} from '../../lib/quoteModel';
import { useAuth } from '../../lib/auth';
import { applyLeadChatPatch } from '../../lib/localChat';
import { sendLeadFirstTouch } from '../../lib/leadFirstTouch';
import { useToast } from '../Toast';
import LeadStageStrip from '../LeadStageStrip';
import ChatSurface from '../ChatSurface';
import { BookConsultPanel } from '../../features/consulting/components/BookConsultPanel';
import { subscribeConsultingFlag } from '../../features/consulting/lib/flags';
import {
  LEAD_STATE_LABEL,
  nextStates,
  type Lead,
  type LeadState,
} from '../../lib/leads';
import type { FStage } from '../../lib/leadPipeline';
import type {
  ClientMatch,
  LiveProject,
} from '../../lib/projectStore';

type CenterTab = 'overview' | 'timeline' | 'book' | 'quote';

const TABS: { key: CenterTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'timeline', label: 'Timeline' },
  { key: 'book', label: 'Book & discovery' },
  { key: 'quote', label: 'Quote' },
];

export interface LeadRecordPageProps {
  lead: Lead;
  onChange: (l: Lead) => void;
  /** Signed-in person (author of staff messages / chat). */
  actorName: string;
  isTeamView: boolean;
  isAdmin: boolean;
  mayTrash: boolean;
  onTrash: (l: Lead) => void;
  knownClients: unknown[];
  knownPartners: unknown[];
  liveProjects: LiveProject[];
  clientMatch: ClientMatch | null;
  contactPool: unknown[];
  /** false → the stage strip is read-only (no commit rights). */
  canCommit: boolean;
  /** Whether money internals (budget/price) are visible to this role. */
  seesMoney: boolean;
  isMobile: boolean;
  /** Board-shaped stage handlers (reused from LeadsLenses onBoardState/Fstage). */
  onState: (lead: Lead, to: LeadState) => void;
  onFstage: (lead: Lead, to: FStage) => void;
  /** Back to the board (selection persists → peek reopens). */
  onBack: () => void;
}

/* ── shared token-based style atoms (approximating Screen 2) ─────────────── */
const regionTag: CSSProperties = {
  flex: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  padding: '10px 18px',
  borderBottom: '1px solid var(--rule, #E5E7EB)',
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--ink-mute, #6B7280)',
  textTransform: 'uppercase',
  letterSpacing: '.05em',
  background: 'var(--paper, #fff)',
};
const regionPin: CSSProperties = {
  marginLeft: 'auto',
  fontSize: 9.5,
  fontWeight: 700,
  color: 'var(--ink-mute, #6B7280)',
  background: 'var(--inset, #F6F7F9)',
  border: '1px solid var(--rule, #E5E7EB)',
  borderRadius: 'var(--r-pill, 999px)',
  padding: '2px 8px',
  textTransform: 'none',
};
const regionScroll: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  scrollbarGutter: 'stable',
  padding: 18,
};
const propGroupKey: CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  color: 'var(--ink-mute, #6B7280)',
  textTransform: 'uppercase',
  letterSpacing: '.05em',
  marginBottom: 9,
};
const propItemRow: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  padding: '8px 0',
  borderBottom: '1px solid var(--inset, #F0F1F3)',
};
const propItemKey: CSSProperties = { fontSize: 11, color: 'var(--ink-mute, #6B7280)' };
const propItemVal: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 600,
  color: 'var(--ink, #111827)',
};

/** One key/value line; renders an em-dash when the value is absent. */
function PropItem({ k, v }: { k: string; v?: string | null }) {
  return (
    <div style={propItemRow}>
      <div style={propItemKey}>{k}</div>
      <div style={propItemVal}>{v && String(v).trim() ? v : '—'}</div>
    </div>
  );
}

function initialOf(name: string): string {
  const t = (name || '?').trim();
  return t ? t[0].toUpperCase() : '?';
}

export default function LeadRecordPage({
  lead,
  onChange,
  actorName,
  isTeamView,
  liveProjects,
  clientMatch,
  canCommit,
  seesMoney,
  isMobile,
  onState,
  onFstage,
  onBack,
}: LeadRecordPageProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [tab, setTab] = useState<CenterTab>('book');
  // First-touch email send state (the business-identity `sendLeadFirstTouch`
  // callable — one send at a time, so the button can't double-fire).
  const [firstTouchSending, setFirstTouchSending] = useState(false);

  // Consulting Door-A flag (mirrors Leads.tsx): local opt-in echo + live
  // settings/flags subscription for the signed-in uid.
  const [consultingEnabled, setConsultingEnabled] = useState(false);
  useEffect(
    () => subscribeConsultingFlag(user?.uid ?? null, setConsultingEnabled),
    [user?.uid],
  );

  // AD-039 identity join — the lead's inbound conversation, looked up via
  // the existing waListConversations callable (the conversation index is
  // server-only; this is the sanctioned read). Team view only; best-effort
  // (null → the quiet "no conversation yet" line).
  // undefined = still looking; null = looked, none joined.
  //
  // STREAMLINE Phase-1 integration (2026-07-03): the join is now STICKY —
  // the lookup PREFERS the lead's stored `waThreadKey` (falling back to the
  // computed exact-phone match if the stored thread vanished), and a FRESH
  // computed match is persisted onto the lead (`waThreadKey` +
  // `threadLinkSource:'matched'` — snapshot fields, no rules change) through
  // the SAME onChange → updateLead seam every other lead write uses. One
  // persist per lead per mount; commit-gated.
  const [convRef, setConvRef] = useState<LeadThreadRef | null | undefined>(
    undefined,
  );
  const persistedJoinFor = useRef<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setConvRef(undefined);
    if (!isTeamView) {
      setConvRef(null);
      return;
    }
    void fetchThreadLookupForLead(lead).then((lookup) => {
      if (cancelled) return;
      setConvRef(lookup.ref);
      if (
        lookup.via === 'computed' &&
        lookup.ref &&
        !lead.waThreadKey &&
        canCommit &&
        persistedJoinFor.current !== lead.id
      ) {
        persistedJoinFor.current = lead.id;
        onChange({
          ...lead,
          waThreadKey: lookup.ref.key,
          threadLinkSource: 'matched',
          updatedAt: new Date().toISOString(),
        });
      }
    });
    return () => {
      cancelled = true;
    };
    // Re-look-up only when the identity that keys the join changes (or the
    // stored key lands — that re-run resolves via 'stored' and never re-writes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id, lead.phone, lead.waThreadKey, isTeamView]);

  // First-touch EMAIL send — the business-identity `sendLeadFirstTouch`
  // callable (Brevo, care@aayatanveda.in, idempotent, writes a lead
  // communications entry). Replaces the old personal-mailto door: the send now
  // goes from the business identity and is logged. Honest results: an
  // already-sent lead is told so; a no-email precondition failure surfaces the
  // server's plain message (never a silent no-op).
  async function onSendFirstTouch(): Promise<void> {
    if (firstTouchSending) return;
    setFirstTouchSending(true);
    try {
      const res = await sendLeadFirstTouch({ leadId: lead.id });
      if (res.emailDispatch === 'sent') {
        toast('First-touch email sent from care@aayatanveda.in.', { ok: true });
      } else if (res.emailDispatch === 'already_sent') {
        toast('A first-touch email was already sent to this lead.');
      } else {
        toast(`First-touch email could not be sent (${res.reason || 'unknown'}).`);
      }
    } catch (e) {
      toast(`First-touch failed: ${(e as Error).message}`);
    } finally {
      setFirstTouchSending(false);
    }
  }

  // The next legal spine state → a plain-language hint in the stage box.
  const nexts = nextStates(lead.state);
  const nextHint =
    nexts.length > 0
      ? `Next: ${nexts.map((s) => LEAD_STATE_LABEL[s]).join(' / ')} →`
      : 'No further stage — terminal';

  // Budget band (money-gated): prefer the agreed price, else the auto-quote.
  const budgetBand =
    !seesMoney
      ? null
      : lead.noCharge
      ? 'No charge'
      : lead.agreedValue != null
      ? `${lead.agreedCurrency === 'USD' ? '$' : '₹'}${lead.agreedValue.toLocaleString()}`
      : lead.quote?.valueINR != null
      ? `₹${lead.quote.valueINR.toLocaleString()}`
      : null;

  // Linked project — first live project that resolves to this lead's matched
  // client (read-only; no project is minted here).
  const linkedProject: LiveProject | null =
    clientMatch
      ? liveProjects.find(
          (p) => p.clientId === clientMatch.cid || p.clientNo === clientMatch.clientNo,
        ) ?? null
      : null;

  /* ── center-tab roving arrow keys (a11y plus) ─────────────────────────── */
  function onTabKeyDown(e: ReactKeyboardEvent<HTMLButtonElement>, idx: number) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const next = (idx + dir + TABS.length) % TABS.length;
    setTab(TABS[next].key);
    const host = e.currentTarget.parentElement;
    const btn = host?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next];
    btn?.focus();
  }

  /* ── styles that depend on layout (mobile stacks) ─────────────────────── */
  const colsStyle: CSSProperties = isMobile
    ? {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        background: 'var(--rule, #E5E7EB)',
        gap: 1,
      }
    : {
        flex: 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: '24% 1fr 26%',
        background: 'var(--rule, #E5E7EB)',
        gap: 1,
      };
  const colRegion: CSSProperties = {
    background: 'var(--paper, #fff)',
    display: 'flex',
    flexDirection: 'column',
    minHeight: isMobile ? undefined : 0,
    minWidth: 0,
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        background: 'var(--paper, #fff)',
      }}
    >
      {/* ── TOP record-bar ─────────────────────────────────────────────── */}
      <div
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '14px 24px',
          background: 'var(--paper, #fff)',
          borderBottom: '1px solid var(--rule, #E5E7EB)',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 42,
            height: 42,
            borderRadius: '50%',
            flex: 'none',
            background: 'var(--accent, #008069)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 17,
          }}
        >
          {initialOf(lead.name)}
        </div>
        <div style={{ minWidth: 0 }}>
          <h4
            style={{
              fontSize: 17,
              fontWeight: 700,
              lineHeight: 1.1,
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {lead.name}
          </h4>
          <div style={{ fontSize: 12, color: 'var(--ink-mute, #6B7280)', marginTop: 2 }}>
            <button
              type="button"
              onClick={onBack}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                font: 'inherit',
                color: 'var(--ink-soft, #374151)',
                fontWeight: 600,
              }}
            >
              Leads
            </button>
            <span style={{ margin: '0 6px', color: 'var(--rule-strong, #D1D5DB)' }}>›</span>
            <span aria-current="page">{lead.name}</span>
            {lead.location ? (
              <>
                <span style={{ margin: '0 6px', color: 'var(--rule-strong, #D1D5DB)' }}>·</span>
                {lead.location}
              </>
            ) : null}
            {lead.propertyType ? (
              <>
                <span style={{ margin: '0 6px', color: 'var(--rule-strong, #D1D5DB)' }}>·</span>
                {lead.propertyType}
              </>
            ) : null}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {/* stage-advance — the SAME guarded write path as the peek. */}
        <div style={{ flex: '0 0 auto', overflowX: 'auto', maxWidth: '52%' }}>
          <LeadStageStrip
            lead={lead}
            onState={(to) => onState(lead, to)}
            onFstage={(to) => onFstage(lead, to)}
            disabled={!canCommit}
          />
        </div>
      </div>

      {/* ── 3-COLUMN body ──────────────────────────────────────────────── */}
      <div style={colsStyle}>
        {/* LEFT — Properties & actions */}
        <div style={colRegion}>
          <div style={regionTag}>
            Properties &amp; actions
            <span style={regionPin}>left · 24%</span>
          </div>
          <div style={regionScroll}>
            {/* stage box */}
            <div
              style={{
                background: 'var(--warn-wash, #FEF3C7)',
                border: '1px solid var(--warn-line, #FDE68A)',
                borderRadius: 'var(--r-md, 12px)',
                padding: '12px 14px',
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: 'var(--warn-ink, #92400E)',
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                }}
              >
                Stage
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--warn-ink, #92400E)', marginTop: 3 }}>
                {LEAD_STATE_LABEL[lead.state]}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--warn-ink, #92400E)', marginTop: 5 }}>
                {nextHint}
              </div>
            </div>

            {/* Owner & contact */}
            <div style={{ marginBottom: 18 }}>
              <div style={propGroupKey}>Owner &amp; contact</div>
              <PropItem k="Owner" v={lead.ownerName} />
              <PropItem k="Phone" v={lead.phone} />
              <PropItem k="Location" v={lead.location} />
              <PropItem k="Source" v={SOURCE_LABEL[lead.source] ?? lead.source} />
            </div>

            {/* Qualify */}
            <div style={{ marginBottom: 18 }}>
              <div style={propGroupKey}>Qualify</div>
              <PropItem k="Project type" v={lead.propertyType} />
              {seesMoney && <PropItem k="Budget band" v={budgetBand} />}
              <PropItem k="Timeline" v={lead.concern} />
            </div>

            {/* Quick actions */}
            <div>
              <div style={propGroupKey}>Quick actions</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* ── Phase 3b FIRST TOUCH (2026-07-03) — a NEW lead (enquired,
                    no outbound yet) gets a one-click first-touch door through
                    the REAL send machinery:
                    · a joined WhatsApp thread → deep link into the Team Inbox
                      with the REAL SendPicker already open (canned messages
                      24h-window aware, approved templates otherwise) —
                      /messages?c=<key>&picker=1. The portal cannot INITIATE a
                      brand-new WA thread (sendManualReply requires an existing
                      conversationKey; business-initiated sends are a
                      functions-side capability), so the thread door shows only
                      when the join resolved one.
                    · no thread but an email → send the business-identity
                      first-touch email via the `sendLeadFirstTouch` callable
                      (Brevo, care@aayatanveda.in, idempotent, logged on the
                      lead) — NOT a personal mailto.
                    · neither → muted text, never a dead button. ── */}
                {lead.state === 'enquired' && !lead.sentAt && !lead.consultBookedAt && (
                  convRef === undefined ? (
                    /* join still resolving — don't offer the email door yet
                       (Vera LOW 2026-07-04: a lead WITH a thread briefly saw
                       the mailto branch before the lookup landed). */
                    <span style={{ fontSize: 11.5, color: 'var(--ink-mute, #6B7280)' }}>
                      First touch — looking for this lead's WhatsApp thread…
                    </span>
                  ) : convRef ? (
                    <button
                      type="button"
                      style={qaPrimary}
                      data-testid="first-touch-wa"
                      onClick={() =>
                        navigate(`/messages?c=${encodeURIComponent(convRef.key)}&picker=1`)
                      }
                      title="Opens this lead's WhatsApp thread in the Team Inbox with the send picker up (24h-window aware)"
                    >
                      Send first touch (WhatsApp) →
                    </button>
                  ) : lead.email ? (
                    <button
                      type="button"
                      style={qaPrimary}
                      data-testid="first-touch-email"
                      disabled={firstTouchSending}
                      onClick={onSendFirstTouch}
                      title="Sends a warm first-touch email from the business identity (care@aayatanveda.in) via Brevo — logged on the lead"
                    >
                      {firstTouchSending ? 'Sending…' : 'Send first touch (email) →'}
                    </button>
                  ) : (
                    <span style={{ fontSize: 11.5, color: 'var(--ink-mute, #6B7280)' }}>
                      First touch: no WhatsApp thread or email on this lead yet —
                      capture a phone/email above, or call{' '}
                      {lead.phone ? <b>{lead.phone}</b> : 'them'} directly.
                    </span>
                  )
                )}
                <button type="button" style={qaPrimary} onClick={() => setTab('book')}>
                  Book consult
                </button>
                {/* STREAMLINE 1c — the SENDER side of the /quotes
                    `newQuoteLeadId` nav-state hook: opens the quote composer
                    PRE-FILLED from this lead (compose-first; nothing written
                    until Save). Quick set-price stays the lightweight door —
                    two doors, one agreed price. */}
                <button
                  type="button"
                  style={qaGhost}
                  onClick={() =>
                    navigate('/quotes', { state: { newQuoteLeadId: lead.id } })
                  }
                >
                  Create quote
                </button>
                {/* Convert-to-client: present per Screen 2. Deep convert (client
                    + project creation) still runs via the peek's <LeadDetail>;
                    this affordance routes the user back there rather than
                    faking a success state. */}
                <button
                  type="button"
                  style={qaGhost}
                  onClick={onBack}
                  title="Convert runs in the lead record — opens in the peek"
                >
                  Convert to client
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* CENTER — tabs + body */}
        <div style={colRegion}>
          <div
            role="tablist"
            aria-label="Lead record sections"
            style={{
              flex: 'none',
              display: 'flex',
              gap: 2,
              padding: '0 18px',
              borderBottom: '1px solid var(--rule, #E5E7EB)',
              background: 'var(--paper, #fff)',
              overflowX: 'auto',
            }}
          >
            {TABS.map((t, i) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  id={`lead-record-tab-${t.key}`}
                  role="tab"
                  type="button"
                  aria-selected={active}
                  aria-controls={`lead-record-panel-${t.key}`}
                  tabIndex={active ? 0 : -1}
                  onClick={() => setTab(t.key)}
                  onKeyDown={(e) => onTabKeyDown(e, i)}
                  style={{
                    font: 'inherit',
                    fontSize: 13,
                    fontWeight: active ? 700 : 600,
                    color: active ? 'var(--accent-deep, #075E54)' : 'var(--ink-mute, #6B7280)',
                    padding: '14px 14px 12px',
                    cursor: 'pointer',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: `2.5px solid ${active ? 'var(--accent, #008069)' : 'transparent'}`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <div
            style={regionScroll}
            role="tabpanel"
            id={`lead-record-panel-${tab}`}
            aria-labelledby={`lead-record-tab-${tab}`}
            tabIndex={0}
          >
            {tab === 'overview' && <OverviewTab lead={lead} seesMoney={seesMoney} budgetBand={budgetBand} />}
            {tab === 'timeline' && <TimelineTab lead={lead} />}
            {tab === 'book' && (
              consultingEnabled ? (
                <BookConsultPanel
                  lead={{
                    id: lead.id,
                    name: lead.name,
                    city: lead.location,
                    projectType: lead.propertyType,
                  }}
                  hostName="Rohit"
                />
              ) : (
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--ink-mute, #6B7280)',
                    border: '1px dashed var(--rule, #E5E7EB)',
                    borderRadius: 'var(--r-md, 12px)',
                    padding: 18,
                    lineHeight: 1.5,
                  }}
                >
                  Live consult booking (Door A) is off for your account. The
                  status-based booking controls remain available in the peek
                  panel.
                </div>
              )
            )}
            {tab === 'quote' && <QuoteTab lead={lead} seesMoney={seesMoney} navigate={navigate} />}
          </div>
        </div>

        {/* RIGHT — Shared conversation */}
        <div style={colRegion}>
          <div style={regionTag}>
            Shared conversation
            <span style={regionPin}>right · 26%</span>
          </div>
          {/* Linked block */}
          <div
            style={{
              flex: 'none',
              padding: '13px 16px',
              borderBottom: '1px solid var(--rule, #E5E7EB)',
              background: 'var(--inset, #F6F7F9)',
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--ink-mute, #6B7280)',
                textTransform: 'uppercase',
                letterSpacing: '.06em',
                marginBottom: 9,
              }}
            >
              Linked
            </div>
            <AssocRow
              k="Client"
              v={clientMatch ? `${clientMatch.name} · ${clientMatch.clientNo}` : 'No linked client yet'}
              dim={!clientMatch}
            />
            <AssocRow
              k="Project"
              v={
                linkedProject
                  ? linkedProject.projectName || linkedProject.vp || 'Live project'
                  : 'No linked project yet'
              }
              dim={!linkedProject}
            />
            {/* AD-039 — the joined inbound conversation (exact phone match).
                Compact: one-line preview + timestamp + channel + deep link.
                No chat mount here, no inner scroll — the full thread lives
                in the Team Inbox. */}
            {convRef !== undefined && (
              <div style={{ padding: '7px 0 2px' }}>
                <div style={{ fontSize: 10.5, color: 'var(--ink-mute, #6B7280)' }}>
                  Conversation
                </div>
                {convRef ? (
                  <>
                    <div
                      style={{
                        fontSize: 12.5,
                        fontWeight: 650,
                        color: 'var(--ink, #111827)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginTop: 2,
                      }}
                      title={convRef.lastMessage || undefined}
                    >
                      {convRef.lastMessage || '—'}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginTop: 3,
                        fontSize: 11,
                        color: 'var(--ink-mute, #6B7280)',
                      }}
                    >
                      <span>WhatsApp</span>
                      {convRef.lastMessageAt ? (
                        <span>· {fmtClock(convRef.lastMessageAt)}</span>
                      ) : null}
                      <Link
                        to={`/messages?c=${encodeURIComponent(convRef.key)}`}
                        style={{
                          marginLeft: 'auto',
                          fontSize: 11.5,
                          fontWeight: 650,
                          color: 'var(--accent-deep, #075E54)',
                          textDecoration: 'none',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Open in Team Inbox →
                      </Link>
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      fontSize: 12.5,
                      fontWeight: 650,
                      color: 'var(--ink-mute, #6B7280)',
                      marginTop: 2,
                    }}
                  >
                    No conversation yet
                  </div>
                )}
              </div>
            )}
          </div>
          {/* the SAME chat the peek uses */}
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <ChatSurface
              title={`Assist · ${lead.name} (${lead.id})`}
              note="Proposals land in the record only after you confirm"
              authorName={actorName}
              isTeamView={isTeamView}
              // Parity GAP 4 fix (2026-07-03): proposals apply through the ONE
              // chat-patch translator (localChat.applyLeadChatPatch) — an
              // `action:'qualify'` performs the REAL transition and
              // `book_consult` runs the spine's booking seam, instead of the
              // old raw spread writing junk fields onto the lead.
              onApply={(patch) => {
                const r = applyLeadChatPatch(
                  lead,
                  patch as Record<string, unknown>,
                  new Date().toISOString(),
                );
                if (r.changed) onChange(r.lead);
              }}
              surface="lead"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── quick-action button styles ──────────────────────────────────────────── */
const qaGhost: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  textAlign: 'left',
  font: 'inherit',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--ink, #111827)',
  background: 'var(--paper, #fff)',
  border: '1px solid var(--rule, #E5E7EB)',
  borderRadius: 'var(--r-sm, 8px)',
  padding: '11px 13px',
  cursor: 'pointer',
};
const qaPrimary: CSSProperties = {
  ...qaGhost,
  justifyContent: 'center',
  background: 'var(--accent, #008069)',
  color: '#fff',
  borderColor: 'var(--accent, #008069)',
};

const SOURCE_LABEL: Record<string, string> = {
  website: 'Website',
  free_tool: 'Free tool',
  manual_referral: 'Referral',
  whatsapp: 'WhatsApp inbound',
};

/* ── associations row ─────────────────────────────────────────────────────── */
function AssocRow({ k, v, dim }: { k: string; v: string; dim?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10.5, color: 'var(--ink-mute, #6B7280)' }}>{k}</div>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 650,
            color: dim ? 'var(--ink-mute, #6B7280)' : 'var(--ink, #111827)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {v}
        </div>
      </div>
    </div>
  );
}

/* ── center-tab bodies ────────────────────────────────────────────────────── */
function summaryRow(k: string, v?: string | null) {
  return (
    <div
      key={k}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 16,
        padding: '9px 0',
        borderBottom: '1px solid var(--inset, #F0F1F3)',
      }}
    >
      <span style={{ fontSize: 12.5, color: 'var(--ink-mute, #6B7280)' }}>{k}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink, #111827)', textAlign: 'right' }}>
        {v && String(v).trim() ? v : '—'}
      </span>
    </div>
  );
}

function OverviewTab({
  lead,
  seesMoney,
  budgetBand,
}: {
  lead: Lead;
  seesMoney: boolean;
  budgetBand: string | null;
}) {
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Lead summary</div>
      {summaryRow('Name', lead.name)}
      {summaryRow('Lead id', lead.id)}
      {summaryRow('Stage', LEAD_STATE_LABEL[lead.state])}
      {summaryRow('Source', SOURCE_LABEL[lead.source] ?? lead.source)}
      {summaryRow('Phone', lead.phone)}
      {summaryRow('Email', lead.email)}
      {summaryRow('Location', lead.location)}
      {summaryRow('Project type', lead.propertyType)}
      {summaryRow('Concern / timeline', lead.concern)}
      {seesMoney && summaryRow('Budget band', budgetBand)}
      {lead.notes ? (
        <div style={{ marginTop: 16 }}>
          <div style={propGroupKey}>Notes</div>
          <div style={{ fontSize: 13, color: 'var(--ink-soft, #374151)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {lead.notes}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function fmtDate(iso?: string | null): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  return new Date(t).toLocaleString();
}

function TimelineTab({ lead }: { lead: Lead }) {
  const rows: { label: string; at: string | null }[] = [
    { label: 'Created', at: fmtDate(lead.createdAt) },
    { label: 'Last updated', at: fmtDate(lead.updatedAt) },
    { label: 'Consult booked', at: fmtDate(lead.consultBookedAt) },
    { label: 'Discovery done', at: fmtDate(lead.discoveryDoneAt) },
    { label: 'Quote sent', at: fmtDate(lead.sentAt) },
  ].filter((r) => r.at);

  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Timeline</div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-mute, #6B7280)' }}>No recorded milestones yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {rows.map((r) => (
            <div
              key={r.label}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '9px 0' }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'var(--accent, #008069)',
                  marginTop: 5,
                  flex: 'none',
                }}
              />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink, #111827)' }}>{r.label}</div>
                <div style={{ fontSize: 11.5, color: 'var(--ink-mute, #6B7280)' }}>{r.at}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 16, fontSize: 11.5, color: 'var(--ink-mute, #6B7280)', fontStyle: 'italic' }}>
        A deeper activity feed (per-message + audit) is a follow-up.
      </div>
    </div>
  );
}

function QuoteTab({
  lead,
  seesMoney,
  navigate,
}: {
  lead: Lead;
  seesMoney: boolean;
  navigate: NavigateFunction;
}) {
  const hasAgreed = lead.agreedValue != null || lead.noCharge;
  const agreed = lead.noCharge
    ? `No charge${lead.noChargeReason ? ` · ${lead.noChargeReason}` : ''}`
    : lead.agreedValue != null
    ? `${lead.agreedCurrency === 'USD' ? '$' : '₹'}${lead.agreedValue.toLocaleString()}`
    : null;

  // STREAMLINE 1c — the lead record shows ITS quotes inline (id · total ·
  // status), live off the existing per-lead query. Click-through opens the
  // quote's detail in /quotes (`openQuoteId` nav-state). Compact rows in the
  // one page flow — no inner scroll box.
  const [leadQuotes, setLeadQuotes] = useState<Quote[] | null>(null);
  useEffect(() => {
    const unsub = listenQuotesForLead(
      lead.id,
      (rows) => setLeadQuotes(rows),
      () => setLeadQuotes([]),
    );
    return unsub;
  }, [lead.id]);

  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Quote</div>
      {!seesMoney ? (
        <div style={{ fontSize: 13, color: 'var(--ink-mute, #6B7280)' }}>
          Pricing is not visible for your role.
        </div>
      ) : (
        <>
          {summaryRow('Agreed price', hasAgreed ? agreed : null)}
          {summaryRow('Standard (rate card)', lead.quote?.valueINR != null ? `₹${lead.quote.valueINR.toLocaleString()}` : null)}
          {summaryRow('Sent', lead.sentAt ? fmtDate(lead.sentAt) : 'Not sent')}
        </>
      )}

      {/* quotes raised from this lead — the same story /quotes tells */}
      <div style={{ marginTop: 18 }}>
        <div style={propGroupKey}>Quotes on this lead</div>
        {leadQuotes === null ? (
          <div style={{ fontSize: 12.5, color: 'var(--ink-mute, #6B7280)' }}>
            Loading quotes…
          </div>
        ) : leadQuotes.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--ink-mute, #6B7280)' }}>
            No quotes raised from this lead yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {leadQuotes.map((q) => {
              const total = quoteTotals(q.lines, q.currency, q.gst, q.discount, quoteAdjustments(q)).grandTotal;
              return (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => navigate('/quotes', { state: { openQuoteId: q.id } })}
                  title={`Open quote ${q.id} in the Quotes module`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    textAlign: 'left',
                    font: 'inherit',
                    fontSize: 12.5,
                    padding: '9px 2px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '1px solid var(--inset, #F0F1F3)',
                    cursor: 'pointer',
                    color: 'var(--ink, #111827)',
                  }}
                >
                  <span style={{ fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>
                    {q.id}
                  </span>
                  <span
                    style={{
                      fontSize: 9.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '.03em',
                      padding: '1px 8px',
                      borderRadius: 'var(--r-pill, 999px)',
                      border: '1px solid var(--rule, #E5E7EB)',
                      color:
                        q.status === 'accepted' || q.status === 'paid'
                          ? 'var(--good, #15803D)'
                          : q.status === 'sent'
                          ? 'var(--warn, #B45309)'
                          : 'var(--ink-soft, #4B5563)',
                    }}
                  >
                    {QUOTE_STATUS_LABEL[q.status]}
                  </span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontWeight: 650,
                      fontVariantNumeric: 'tabular-nums',
                      color: 'var(--ink, #111827)',
                    }}
                  >
                    {seesMoney ? quoteMoney(total, q.currency) : '•••'}
                  </span>
                  <span aria-hidden="true" style={{ color: 'var(--ink-mute, #6B7280)' }}>
                    →
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
        <button
          type="button"
          style={{ ...qaGhost, width: 'auto', display: 'inline-flex' }}
          onClick={() => navigate('/quotes', { state: { newQuoteLeadId: lead.id } })}
        >
          Create quote from this lead →
        </button>
        <button
          type="button"
          style={{ ...qaGhost, width: 'auto', display: 'inline-flex' }}
          onClick={() => navigate('/quotes')}
        >
          Open Quotes module →
        </button>
      </div>
      <div style={{ marginTop: 12, fontSize: 11.5, color: 'var(--ink-mute, #6B7280)', fontStyle: 'italic' }}>
        The full quote builder (line items, send, accept) lives in the Quotes module.
      </div>
    </div>
  );
}
